'use client';

import type { SeverityDataItem, CategoryItem } from './types';
import styles from '@/components/audit/ProReportResults.module.css';

interface ExecutiveSummaryProps {
  total: number;
  severityData: SeverityDataItem[];
  categories: CategoryItem[];
  activeFilter: string | null;
  onFilterChange: (filter: string | null) => void;
}

export function ExecutiveSummary({
  total,
  severityData,
  categories,
  activeFilter,
  onFilterChange,
}: ExecutiveSummaryProps) {
  return (
    <section className={styles.executiveSummary}>
      <div className={styles.summaryHeader}>
        <h2 className={styles.sectionTitle}>Executive Summary</h2>
        <p className={styles.summaryIntro}>
          Your site was analyzed against <strong>167 research-backed Audit Criteria</strong>
        </p>
      </div>

      <div className={styles.summaryGrid}>
        {/* Score Visualization */}
        <div className={styles.scoreVisualization}>
          <div className={styles.donutChart}>
            <svg viewBox="0 0 100 100" className={styles.donutSvg}>
              {/* Background circle */}
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke="var(--audit-border-subtle)"
                strokeWidth="8"
              />
              {/* Severity arcs */}
              {severityData.map((item, index) => {
                const offset = severityData.slice(0, index).reduce((acc, s) => acc + s.count, 0);
                const percentage = (item.count / total) * 100;
                const circumference = 2 * Math.PI * 40;
                const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
                const rotation = (offset / total) * 360 - 90;

                if (item.count === 0) return null;

                return (
                  <circle
                    key={item.label}
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke={item.color}
                    strokeWidth="8"
                    strokeDasharray={strokeDasharray}
                    strokeLinecap="round"
                    transform={`rotate(${rotation} 50 50)`}
                    className={styles.donutSegment}
                  />
                );
              })}
            </svg>
            <div className={styles.donutCenter}>
              <span className={styles.donutNumber}>{total}</span>
              <span className={styles.donutLabel}>Issues</span>
            </div>
          </div>

          <div className={styles.severityLegend}>
            {severityData.map((item) => (
              <button
                key={item.label}
                className={`${styles.legendItem} ${activeFilter === item.label.toLowerCase() ? styles.legendActive : ''}`}
                onClick={() =>
                  onFilterChange(
                    activeFilter === item.label.toLowerCase() ? null : item.label.toLowerCase()
                  )
                }
                style={{ '--legend-color': item.color } as React.CSSProperties}
              >
                <span className={styles.legendDot} />
                <span className={styles.legendLabel}>{item.label}</span>
                <span className={styles.legendCount}>{item.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Category Breakdown */}
        <div className={styles.categoryBreakdown}>
          <h3 className={styles.breakdownTitle}>Top Areas for Improvement</h3>
          <div className={styles.categoryList}>
            {categories.slice(0, 5).map((cat, index) => (
              <div key={cat.name} className={styles.categoryItem}>
                <div className={styles.categoryRank}>{index + 1}</div>
                <div className={styles.categoryIcon}>{cat.icon}</div>
                <div className={styles.categoryInfo}>
                  <span className={styles.categoryName}>{cat.name}</span>
                  <span className={styles.categoryCount}>{cat.count} issues</span>
                </div>
                <div className={styles.categoryBar}>
                  <div
                    className={styles.categoryBarFill}
                    style={{ width: `${(cat.count / categories[0].count) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
